export const BIRTHDATE_MIN_AGE = 0;
export const BIRTHDATE_MAX_AGE = 150;

export type BirthdateAgeLimits = {
  min_age: number | null;
  max_age: number | null;
};

export type DateCalendarBounds = {
  min?: string;
  max?: string;
};

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;
const DOT_DATE = /^(\d{2})\.(\d{2})\.(\d{4})$/;

export function readOptionalAge(raw: unknown): number | null {
  if (raw == null || raw === "") return null;
  if (typeof raw !== "number" && typeof raw !== "string") return null;
  if (typeof raw === "string" && raw.trim() === "") return null;
  const numeric = Number(raw);
  if (!Number.isFinite(numeric)) return null;
  const age = Math.trunc(numeric);
  if (age < BIRTHDATE_MIN_AGE || age > BIRTHDATE_MAX_AGE) return null;
  return age;
}

export function isValidYmd(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

export function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function subtractYears(date: Date, years: number): Date {
  const month = date.getMonth();
  const day = date.getDate();
  const result = new Date(date.getFullYear(), month, day);
  result.setFullYear(result.getFullYear() - years);
  if (result.getMonth() !== month) {
    result.setDate(0);
  }
  return result;
}

export function parseCalendarDate(value: string): Date | null {
  const raw = value.trim();
  if (!raw) return null;

  const iso = raw.match(ISO_DATE) ?? raw.match(/^(\d{4})-(\d{2})-(\d{2})T/);
  if (iso) {
    const year = Number(iso[1]);
    const month = Number(iso[2]);
    const day = Number(iso[3]);
    if (!isValidYmd(year, month, day)) return null;
    return new Date(year, month - 1, day);
  }

  const dotted = raw.match(DOT_DATE);
  if (dotted) {
    const day = Number(dotted[1]);
    const month = Number(dotted[2]);
    const year = Number(dotted[3]);
    if (!isValidYmd(year, month, day)) return null;
    return new Date(year, month - 1, day);
  }

  return null;
}

export function isIsoDateString(value: string): boolean {
  const match = value.trim().match(ISO_DATE);
  if (!match) return false;
  return isValidYmd(Number(match[1]), Number(match[2]), Number(match[3]));
}

export function isIsoDateTimeString(value: string): boolean {
  const match = value.trim().match(ISO_DATE_TIME);
  if (!match) return false;
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = match[6] == null ? 0 : Number(match[6]);
  if (hour > 23 || minute > 59 || second > 59) return false;
  return isValidYmd(Number(match[1]), Number(match[2]), Number(match[3]));
}

/** Submit value for datetime-local: YYYY-MM-DDTHH:mm */
export function normalizeDateTimeSubmit(value: string): string {
  const match = value.trim().match(ISO_DATE_TIME);
  if (!match) return value.trim();
  return `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}`;
}

export function ageAt(birth: Date, asOf: Date): number {
  const birthDay = startOfLocalDay(birth);
  const asOfDay = startOfLocalDay(asOf);
  let age = asOfDay.getFullYear() - birthDay.getFullYear();
  if (
    asOfDay.getMonth() < birthDay.getMonth() ||
    (asOfDay.getMonth() === birthDay.getMonth() &&
      asOfDay.getDate() < birthDay.getDate())
  ) {
    age -= 1;
  }
  return age;
}

export function asOfDate(asOf?: string | Date | null): Date {
  if (asOf instanceof Date && !Number.isNaN(asOf.getTime())) {
    return startOfLocalDay(asOf);
  }
  if (typeof asOf === "string" && asOf.trim()) {
    const parsed = new Date(asOf);
    if (!Number.isNaN(parsed.getTime())) return startOfLocalDay(parsed);
  }
  return startOfLocalDay(new Date());
}

export function ageFromBirthdate(
  value: unknown,
  asOf?: string | Date | null,
): number | null {
  const raw =
    typeof value === "string" ? value : value == null ? "" : String(value);
  const birth = parseCalendarDate(raw);
  if (!birth) return null;
  const asOfDay = asOfDate(asOf);
  if (birth.getTime() > asOfDay.getTime()) return null;
  return ageAt(birth, asOfDay);
}

export function birthdateCalendarBounds(
  limits: BirthdateAgeLimits,
  asOf?: string | Date | null,
): DateCalendarBounds {
  const today = asOfDate(asOf);
  const bounds: DateCalendarBounds = {
    max: toIsoDate(today),
  };

  if (limits.min_age != null) {
    bounds.max = toIsoDate(subtractYears(today, limits.min_age));
  }
  if (limits.max_age != null) {
    bounds.min = toIsoDate(subtractYears(today, limits.max_age));
  }

  return bounds;
}

export function isValidDateSubmit(value: string, withTime: boolean): boolean {
  const raw = value.trim();
  if (!raw) return false;
  return withTime ? isIsoDateTimeString(raw) : isIsoDateString(raw);
}

export function birthdateValidationMessage(
  value: string,
  limits: BirthdateAgeLimits,
  asOf?: string | Date | null,
): string | null {
  const raw = value.trim();
  if (!raw) return "Укажите корректную дату";

  const birth = parseCalendarDate(raw);
  const asOfDay = asOfDate(asOf);
  if (!birth) return "Укажите корректную дату";
  if (birth.getTime() > asOfDay.getTime()) {
    return "Дата рождения не может быть в будущем";
  }

  const age = ageAt(birth, asOfDay);
  const min = limits.min_age;
  const max = limits.max_age;

  if (min != null && max != null && (age < min || age > max)) {
    return `Возраст должен быть от ${min} до ${max} лет`;
  }
  if (min != null && age < min) {
    return `Возраст должен быть не меньше ${min} лет`;
  }
  if (max != null && age > max) {
    return `Возраст должен быть не больше ${max} лет`;
  }

  return null;
}

export function formatAgeLabel(age: number): string {
  const years = Math.trunc(age);
  const mod100 = years % 100;
  const mod10 = years % 10;
  if (mod100 >= 11 && mod100 <= 14) return `${years} лет`;
  if (mod10 === 1) return `${years} год`;
  if (mod10 >= 2 && mod10 <= 4) return `${years} года`;
  return `${years} лет`;
}

export function formatValueWithAge(
  raw: string,
  age: number | null | undefined,
): string {
  if (age == null || !Number.isFinite(age)) return raw;
  return `${raw} (${formatAgeLabel(age)})`;
}
